import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Avatar, Box, Button, Card, CardContent, CardMedia, Chip, Container, Grid, IconButton, Stack, Typography, CircularProgress, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Divider } from '@mui/material';
import { Favorite, Comment, LibraryBooks, WhatsApp, Facebook, Share, FavoriteBorder, Close, Person, Article as ArticleIcon } from '@mui/icons-material';
import blogPreviewData, { fontOptions } from '../../data/blogPreviewData';
import API from '../../services/Config';
import Swal from 'sweetalert2';

const shadeColor = (hex, percent) => {
  let h = hex.replace('#', '');
  if (h.length === 3)
    h = h
      .split('')
      .map((c) => c + c)
      .join('');
  const num = parseInt(h, 16);
  let r = (num >> 16) & 0xff;
  let g = (num >> 8) & 0xff;
  let b = num & 0xff;
  const factor = (100 + percent) / 100;
  r = Math.min(255, Math.max(0, Math.round(r * factor)));
  g = Math.min(255, Math.max(0, Math.round(g * factor)));
  b = Math.min(255, Math.max(0, Math.round(b * factor)));
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
};

const nivelesVisibles = ['COMUNIDAD', 'COMUNIDAD_Y_PACIENTES'];

const isArticuloVisible = (articulo) => {
  if (!articulo) return false;
  const aprobado = articulo.estatusPublicacion?.aprobado === true && articulo.estatusPublicacion?.rechazado !== true;
  return aprobado && nivelesVisibles.includes(articulo.nivelArticulo);
};

const ribbon = (label, sx = {}) => (
  <Box
    sx={{
      position: 'absolute',
      top: 8,
      right: 8,
      bgcolor: 'rgba(59, 130, 246, 0.95)',
      color: '#fff',
      px: 1.5,
      py: 0.5,
      borderRadius: 2,
      fontSize: 11,
      display: 'flex',
      alignItems: 'center',
      gap: 0.5,
      fontWeight: 600,
      ...sx,
    }}>
    <Share fontSize='inherit' />
    {label}
  </Box>
);

const ArticleStats = ({ likes, comentarios, centered = false, sx = {}, onComment }) => {
  const likeCount = Array.isArray(likes) ? likes.length : typeof likes === 'number' ? likes : 0;
  const commentCount = Array.isArray(comentarios) ? comentarios.length : typeof comentarios === 'number' ? comentarios : 0;
  return (
    <Stack direction='row' spacing={2} alignItems='center' justifyContent={centered ? 'center' : 'flex-start'} sx={{ color: '#475569', fontSize: '0.85rem', ...sx }}>
      <Stack direction='row' spacing={0.5} alignItems='center'>
        <Favorite color='inherit' sx={{ fontSize: 16 }} />
        <Typography component='span'>{likeCount}</Typography>
      </Stack>
      <Stack direction='row' spacing={0.5} alignItems='center' sx={{ cursor: onComment ? 'pointer' : 'default' }} onClick={onComment ? () => onComment() : undefined}>
        <Comment color='inherit' sx={{ fontSize: 16 }} />
        <Typography component='span'>{commentCount}</Typography>
      </Stack>
    </Stack>
  );
};

const ArticleCard = ({ article, color, layout = 'horizontal', onClick, onComment }) => {
  const hasImage = Boolean(article.imagenUrl);
  const overlayEnabled = layout !== 'horizontal';
  const cardSx = {
    display: 'flex',
    flexDirection: layout === 'horizontal' ? { xs: 'column', sm: 'row' } : 'column',
    borderRadius: layout === 'vertical' ? 3 : 2,
    border: layout === 'vertical' ? 'none' : '1px solid #e2e8f0',
    cursor: 'default',
    overflow: 'hidden',
    position: 'relative',
    height: overlayEnabled ? '100%' : 'auto',
    minHeight: overlayEnabled ? 364 : 'auto',
    transition: 'box-shadow 0.3s ease, transform 0.3s ease',
    boxShadow: overlayEnabled ? '0 4px 12px rgba(0,0,0,0.05)' : undefined,
    ...(overlayEnabled && {
      '&:hover': { boxShadow: '0 8px 24px rgba(0,0,0,0.1)', transform: 'translateY(-2px)' },
    }),
  };

  return (
    <Card
      elevation={0}
      sx={{
        ...cardSx,
        cursor: onClick ? 'pointer' : cardSx.cursor,
      }}
      onClick={() => onClick && onClick(article, { scrollComments: false })}>
      <Box sx={{ position: 'relative' }}>
        {article.esCompartido && ribbon('Compartido')}
        {hasImage ? (
          <CardMedia
            component='div'
            image={article.imagenUrl}
            sx={{
              width: layout === 'horizontal' ? { sm: 170, xs: '100%' } : '100%',
              height: layout === 'horizontal' ? 140 : 160,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          />
        ) : (
          <Box
            sx={{
              width: layout === 'horizontal' ? { sm: 170, xs: '100%' } : '100%',
              height: layout === 'horizontal' ? 140 : 160,
              background: `linear-gradient(135deg, ${color}10 0%, ${color}20 100%)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
            <LibraryBooks sx={{ fontSize: 40, color: color }} />
          </Box>
        )}
      </Box>

      <CardContent
        sx={{
          flex: 1,
          py: 2,
          px: 3,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          position: 'relative',
          textAlign: layout === 'vertical' ? 'center' : 'left',
        }}>
        <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#111827', display: '-webkit-box', WebkitLineClamp: layout === 'vertical' ? 2 : 1, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{article.titulo}</Typography>
        <Typography
          sx={{
            color: '#64748b',
            fontSize: '0.9rem',
            lineHeight: 1.4,
            display: '-webkit-box',
            WebkitLineClamp: layout === 'vertical' ? 3 : 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}>
          {article.descripcion}
        </Typography>
        <Typography
          sx={{
            fontSize: '0.75rem',
            color: '#64748b',
            mt: 1,
            cursor: 'pointer',
            textDecoration: 'underline',
          }}
          onClick={(e) => {
            e.stopPropagation();
            window.location.href = `https://${article.autorId?.subdomain}.nutrilander.com/blog`;
          }}>
          Por {getAutorNombre(article.autorId)} @{article.autorId?.subdomain}
        </Typography>
        <ArticleStats likes={article.numeroLikes || article.likes} comentarios={article.numeroComentarios || article.comentarios} centered={layout === 'vertical'} sx={{ mt: layout === 'vertical' ? 'auto' : 0 }} onComment={onComment} />
      </CardContent>
    </Card>
  );
};

const ClassicPreview = ({ color, fontFamily, data, onArticuloClick, onComment, emptyState }) => {
  const articulos = data.articulos || [];
  const hasArticles = articulos.length > 0;
  const featured = articulos[0];
  const others = articulos.slice(1);
  const nutriologo = data.nutriologo || {};
  const featuredHeights = { xs: 220, md: 375 };
  const featuredImageHeightXs = 150;

  const sidebarGradient = `linear-gradient(180deg, ${color} 0%, ${shadeColor(color, -15)} 100%)`;

  const openLink = (url) => {
    if (!url) return;
    window.open(url, '_blank');
  };

  return (
    <Box
      sx={{
        width: '100vw',
        ml: 'calc(-50vw + 50%)',
        background: '#edf0f6',
        fontFamily,
        py: 0,
        overflowX: 'hidden',
      }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '1fr 4fr' },
          minHeight: { xs: 'auto', lg: '100vh' },
          width: '100%',
          background: '#f8f9fb',
        }}>
        <Box
          sx={{
            background: sidebarGradient,
            color: '#fff',
            px: { xs: 4, md: 6 },
            pt: { xs: 6, md: 9 },
            pb: { xs: 5, md: 8 },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 3,
          }}>
          {nutriologo.imagen ? (
            <Avatar
              src={nutriologo.imagen}
              sx={{
                width: 140,
                height: 140,
                border: '4px solid rgba(255,255,255,0.35)',
                boxShadow: '0 12px 30px rgba(0,0,0,0.25)',
              }}
            />
          ) : (
            <Avatar
              sx={{
                width: 140,
                height: 140,
                bgcolor: shadeColor(color, 20),
                border: '4px solid rgba(255,255,255,0.35)',
              }}>
              {(nutriologo.nombreAMostrar || nutriologo.nombre || 'N').charAt(0)}
            </Avatar>
          )}
          <Box textAlign='center'>
            <Typography sx={{ fontWeight: 700, fontSize: '1.5rem' }}>{nutriologo.nombreAMostrar || nutriologo.nombre || 'Nutriólogo'}</Typography>
            <Typography sx={{ opacity: 0.85 }}>@{nutriologo.subdomain || 'nutriologo'}</Typography>
          </Box>
          <Stack spacing={1.5} width='100%' maxWidth={260}>
            <Button
              variant='contained'
              onClick={() => (window.location.href = '/login')}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                bgcolor: '#fff',
                color,
                boxShadow: '0 8px 20px rgba(0,0,0,0.15)',
                '&:hover': { bgcolor: '#f8fafc' },
              }}>
              Iniciar sesión
            </Button>
            <Button
              variant='outlined'
              onClick={() => (window.location.href = '/registro')}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                color: '#fff',
                borderColor: 'rgba(255,255,255,0.65)',
                '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.08)' },
              }}>
              Registrarme
            </Button>
          </Stack>
          <Stack direction='row' spacing={1.5}>
            {nutriologo.telefonoAMostrar && nutriologo.telefonoAMostrar.trim() !== '' && (
              <IconButton
                size='large'
                sx={{
                  border: '1px solid rgba(255,255,255,0.35)',
                  color: '#fff',
                  '&:hover': { background: 'rgba(255,255,255,0.15)' },
                }}
                onClick={() => openLink(`https://wa.me/52${nutriologo.telefonoAMostrar}`)}>
                <WhatsApp />
              </IconButton>
            )}
            {nutriologo.urlFacebook && nutriologo.urlFacebook.trim() !== '' && (
              <IconButton
                size='large'
                sx={{
                  border: '1px solid rgba(255,255,255,0.35)',
                  color: '#fff',
                  '&:hover': { background: 'rgba(255,255,255,0.15)' },
                }}
                onClick={() => openLink(nutriologo.urlFacebook.startsWith('http') ? nutriologo.urlFacebook : `https://${nutriologo.urlFacebook}`)}>
                <Facebook />
              </IconButton>
            )}
            <Tooltip title='Ir al inicio'>
              <IconButton
                size='large'
                sx={{
                  border: '1px solid rgba(255,255,255,0.35)',
                  color: '#fff',
                  '&:hover': { background: 'rgba(255,255,255,0.15)' },
                }}
                onClick={() => openLink(`https://${nutriologo.subdomain}.nutrilander.com/perfil`)}>
                <ArticleIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>

        <Box
          sx={{
            px: { xs: 3, md: 6 },
            py: { xs: 4, md: 6 },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 4,
          }}>
          <Typography variant='h4' sx={{ fontWeight: 800, color: '#0f172a', alignSelf: 'flex-start', mb: -1 }}>
            Artículos recientes
          </Typography>

          {data.articulos.length === 0 ? (
            <Box
              sx={{
                textAlign: 'center',
                py: 8,
                px: 4,
                color: '#475569',
                width: '100%',
                bgcolor: '#fff',
                borderRadius: 3,
                boxShadow: '0 20px 45px rgba(15,23,42,0.08)',
              }}>
              <LibraryBooks sx={{ fontSize: 64, color: '#cbd5f5', mb: 2 }} />
              <Typography variant='h6' sx={{ fontWeight: 600, mb: 1 }}>
                No hay artículos
              </Typography>
              <Typography>Los artículos publicados aparecerán aquí.</Typography>
            </Box>
          ) : (
            <>
              {featured && (
                <Box sx={{ width: { xs: '100%', lg: '80%' } }}>
                  <Card
                    elevation={0}
                    onClick={() => onArticuloClick(featured, { scrollComments: false })}
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', md: 'row' },
                      borderRadius: 4,
                      overflow: 'hidden',
                      minHeight: { xs: featuredHeights.xs, md: featuredHeights.md },
                      height: { md: featuredHeights.md },
                      maxHeight: { md: featuredHeights.md },
                      boxShadow: '0 25px 60px rgba(15,23,42,0.12)',
                      cursor: 'pointer',
                      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                      '&:hover': {
                        transform: 'translateY(-6px)',
                        boxShadow: '0 35px 70px rgba(15,23,42,0.2)',
                      },
                    }}>
                    <Box
                      sx={{
                        position: 'relative',
                        flex: { xs: '0 0 auto', md: '0 0 45%' },
                        height: { xs: featuredImageHeightXs, md: '100%' },
                        maxHeight: { md: featuredHeights.md },
                      }}>
                      {featured.esCompartido && ribbon('Compartido', { top: 16, right: 16 })}
                      {featured.imagenUrl ? (
                        <Box
                          component='img'
                          src={featured.imagenUrl}
                          alt={featured.titulo}
                          sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            display: 'block',
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: '100%',
                            height: '100%',
                            maxHeight: { md: featuredHeights.md },
                            minHeight: { xs: featuredImageHeightXs, md: featuredHeights.md },
                            background: `linear-gradient(135deg, ${color}20 0%, ${color}40 100%)`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}>
                          <LibraryBooks sx={{ fontSize: 56, color }} />
                        </Box>
                      )}
                    </Box>
                    <CardContent
                      sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        gap: 2,
                        px: { xs: 2.5, md: 4 },
                        py: { xs: 2, md: 4 },
                        height: { md: '100%' },
                      }}>
                      <Typography
                        sx={{
                          fontSize: { xs: '1.3rem', md: '1.6rem' },
                          fontWeight: 800,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}>
                        {featured.titulo}
                      </Typography>
                      <Typography
                        sx={{
                          color: '#475569',
                          fontSize: '1rem',
                          lineHeight: 1.6,
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}>
                        {featured.descripcion}
                      </Typography>
                      <ArticleStats likes={featured.numeroLikes || featured.likes} comentarios={featured.numeroComentarios || featured.comentarios} onComment={() => onComment(featured)} />
                    </CardContent>
                  </Card>
                </Box>
              )}

              {others.length > 0 && (
                <Box sx={{ width: { xs: '100%', lg: '80%' } }}>
                  <Typography variant='h5' sx={{ fontWeight: 700, mb: 2 }}>
                    Más artículos
                  </Typography>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                      gap: 3,
                      gridAutoRows: 'auto',
                    }}>
                    {others.map((articulo) => (
                      <Card
                        key={articulo._id}
                        onClick={() => onArticuloClick(articulo, { scrollComments: false })}
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          height: '100%',
                          borderRadius: 3,
                          overflow: 'hidden',
                          position: 'relative',
                          boxShadow: '0 18px 35px rgba(15,23,42,0.12)',
                          cursor: 'pointer',
                          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: '0 24px 50px rgba(15,23,42,0.18)',
                          },
                        }}>
                        <Box
                          sx={{
                            width: { xs: 130, sm: 150 },
                            position: 'relative',
                            flexShrink: 0,
                          }}>
                          {articulo.esCompartido && ribbon('Compartido')}
                          {articulo.imagenUrl ? (
                            <Box
                              component='img'
                              src={articulo.imagenUrl}
                              alt={articulo.titulo}
                              sx={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                minHeight: 162,
                              }}
                            />
                          ) : (
                            <Box
                              sx={{
                                width: '100%',
                                height: '100%',
                                minHeight: 162,
                                background: `linear-gradient(135deg, ${color}15, ${color}30)`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}>
                              <LibraryBooks sx={{ fontSize: 42, color: shadeColor(color, -30) }} />
                            </Box>
                          )}
                        </Box>
                        <CardContent
                          sx={{
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                            gap: 1.5,
                            p: 3,
                          }}>
                          <Typography
                            sx={{
                              fontWeight: 700,
                              fontSize: '1rem',
                              color: '#0f172a',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                            }}>
                            {articulo.titulo}
                          </Typography>
                          <Typography
                            sx={{
                              color: '#64748b',
                              fontSize: '0.9rem',
                              lineHeight: 1.5,
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                            }}>
                            {articulo.descripcion}
                          </Typography>
                          <ArticleStats likes={articulo.numeroLikes || articulo.likes} comentarios={articulo.numeroComentarios || articulo.comentarios} onComment={() => onComment(articulo)} />
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

const MinimalPreview = ({ color, fontFamily, data, onArticuloClick, onComment, emptyState }) => {
  const articulos = data.articulos || [];
  const hasArticles = articulos.length > 0;
  const featured = articulos[0];
  const others = articulos.slice(1);
  return (
    <>
      <Box
        sx={{
          background: `linear-gradient(135deg, ${color}15 0%, ${color}25 100%)`,
          textAlign: 'center',
          px: 8.75,
          py: 4.2,
          width: '100vw',
          position: 'relative',
          left: '50%',
          transform: 'translateX(-50%)',
          fontFamily,
        }}>
        <Avatar
          src={data.nutriologo.imagen}
          sx={{
            width: 70,
            height: 70,
            margin: '0 auto',
            mb: 1.4,
            bgcolor: color,
          }}>
          {!data.nutriologo.imagen && data.nutriologo.nombre?.charAt(0)}
        </Avatar>
        <Typography variant='h6' sx={{ fontWeight: 700, fontSize: '0.875rem' }}>
          {data.nutriologo.nombre}
        </Typography>
        <Typography variant='body2' sx={{ color: '#475569', mb: 1.4, fontSize: '0.6125rem' }}>
          @{data.nutriologo.subdomain}
        </Typography>
        <Stack direction='row' spacing={1} justifyContent='center'>
          <Button variant='contained' onClick={() => (window.location.href = '/login')} sx={{ bgcolor: color, color: '#fff', textTransform: 'none', fontWeight: 600, py: 0.7, px: 1.75 }}>
            Iniciar sesión
          </Button>
          <Button variant='outlined' onClick={() => (window.location.href = '/registro')} sx={{ borderColor: color, color, textTransform: 'none', fontWeight: 600, py: 0.7, px: 1.75 }}>
            Registrarme
          </Button>
        </Stack>
      </Box>
      <Box
        sx={{
          borderRadius: 8,
          overflow: 'hidden',
          boxShadow: '0 20px 40px rgba(15,23,42,0.15)',
          background: '#fff',
          fontFamily,
          mt: 4,
        }}>
        <Box sx={{ py: 8, px: { xs: 10.5, md: 25 } }}>
          <Typography variant='h5' sx={{ fontWeight: 800, textAlign: 'center', mb: 4 }}>
            Artículos destacados
          </Typography>
          {hasArticles ? (
            <>
              {featured && (
                <Box sx={{ mb: 4 }}>
                  <ArticleCard article={featured} color={color} layout='vertical' onClick={onArticuloClick} onComment={onComment} />
                </Box>
              )}
              <Grid container spacing={2} alignItems='stretch'>
                {others.map((articulo) => (
                  <Grid item xs={12} sm={12} md={6} lg={4} key={articulo._id} sx={{ display: 'flex' }}>
                    <ArticleCard article={articulo} color={color} layout='vertical' onClick={onArticuloClick} onComment={onComment} />
                  </Grid>
                ))}
              </Grid>
            </>
          ) : (
            emptyState
          )}
        </Box>
      </Box>
    </>
  );
};

const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('es-MX', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const formatDateTime = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('es-MX', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getAutorNombre = (autor) => {
  if (!autor) return 'Nutriólogo';
  if (autor.nombreAMostrar) return autor.nombreAMostrar;
  if (autor.usuario?.nombre && autor.usuario?.apellidos) return `${autor.usuario.nombre} ${autor.usuario.apellidos}`;
  if (autor.nombre && autor.apellido) return `${autor.nombre} ${autor.apellido}`;
  return 'Nutriólogo';
};

const ModernPreview = ({ color, fontFamily, data, onArticuloClick, onComment, emptyState }) => {
  const articulos = data.articulos || [];
  const hasArticles = articulos.length > 0;
  const featured = articulos[0];
  const others = articulos.slice(1);
  return (
    <Box
      sx={{
        borderRadius: 3,
        overflow: 'hidden',
        boxShadow: '0 25px 50px rgba(15,23,42,0.18)',
        background: '#fafbfc',
        fontFamily,
        maxWidth: 'none',
        mx: 'auto',
      }}>
      <Box
        sx={{
          bgcolor: '#fff',
          borderBottom: '1px solid #e5e7eb',
          px: { xs: 3, md: 5 },
          py: 4,
        }}>
        <Stack direction='row' spacing={2} alignItems='center' flexWrap='wrap' justifyContent='space-between' sx={{ gap: 3 }}>
          <Stack direction='row' spacing={2} alignItems='center' sx={{ flex: 1, minWidth: 0 }}>
            <Avatar
              src={data.nutriologo.imagen}
              sx={{
                width: 64,
                height: 64,
                border: `3px solid ${color}`,
                bgcolor: color,
              }}>
              {!data.nutriologo.imagen && data.nutriologo.nombre?.charAt(0)}
            </Avatar>
            <Box>
              <Typography variant='h6' sx={{ fontWeight: 800 }}>
                {data.nutriologo.nombre}
              </Typography>
              <Typography variant='body2' sx={{ color: '#64748b' }}>
                @{data.nutriologo.subdomain}
              </Typography>
            </Box>
          </Stack>
          <Stack direction='row' spacing={1}>
            <Button
              variant='contained'
              onClick={() => (window.location.href = '/login')}
              sx={{
                bgcolor: color,
                color: '#fff',
                textTransform: 'none',
                fontWeight: 600,
              }}>
              Iniciar sesión
            </Button>
            <Button
              variant='outlined'
              onClick={() => (window.location.href = '/registro')}
              sx={{
                borderColor: color,
                color,
                textTransform: 'none',
                fontWeight: 600,
              }}>
              Registrarme
            </Button>
          </Stack>
        </Stack>
      </Box>
      <Box sx={{ px: { xs: 2, md: 5 }, py: { xs: 3, md: 5 } }}>
        {hasArticles ? (
          <>
            {featured && (
              <Box sx={{ mb: 5 }}>
                <Card
                  elevation={0}
                  onClick={() => onArticuloClick(featured)}
                  sx={{
                    borderRadius: 3,
                    overflow: 'hidden',
                    background: '#fff',
                    boxShadow: '0 10px 30px rgba(15,23,42,0.12)',
                    position: 'relative',
                    cursor: 'pointer',
                    height: { xs: 300, md: 350 },
                  }}>
                  <Stack direction={{ xs: 'column', md: 'row' }} sx={{ height: '100%' }}>
                    <Box sx={{ width: { xs: '100%', md: '33.3%' }, position: 'relative', height: '100%' }}>
                      {featured.esCompartido && ribbon('Compartido')}
                      <CardMedia
                        component='div'
                        image={featured.imagenUrl}
                        sx={{
                          height: '100%',
                          backgroundSize: '100% 100%',
                          backgroundPosition: 'center',
                        }}
                      />
                    </Box>
                    <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', py: 3, px: 3 }}>
                      <Box>
                        <Typography sx={{ fontWeight: 800, fontSize: '1.6rem', mb: 1.5, color: '#0f172a' }}>{featured.titulo}</Typography>
                        <Typography sx={{ color: '#475569', mb: 2 }}>{featured.descripcion}</Typography>
                      </Box>
                      <Box>
                        <ArticleStats likes={featured.numeroLikes || featured.likes} comentarios={featured.numeroComentarios || featured.comentarios} onComment={() => onComment(featured)} />
                        <Chip label='Nuevo' size='small' sx={{ mt: 2, bgcolor: color, color: '#fff', fontWeight: 700 }} />
                      </Box>
                    </CardContent>
                  </Stack>
                </Card>
              </Box>
            )}
            <Typography variant='h6' sx={{ fontWeight: 800, mb: 3 }}>
              Más artículos
            </Typography>
            <Grid container spacing={2} alignItems='stretch'>
              {others.map((articulo) => (
                <Grid item xs={12} sm={6} md={4} lg={2} key={articulo._id} sx={{ display: 'flex' }}>
                  <ArticleCard article={articulo} color={color} layout='vertical' onClick={onArticuloClick} onComment={onComment} />
                </Grid>
              ))}
            </Grid>
          </>
        ) : (
          emptyState
        )}
      </Box>
    </Box>
  );
};

const BlogPreviewPage = () => {
  const fallbackNutriologo = blogPreviewData.nutriologo;
  const fallbackConfig = blogPreviewData.config;

  const [nutriologo, setNutriologo] = useState(fallbackNutriologo);
  const [articulos, setArticulos] = useState([]);
  const [config, setConfig] = useState(fallbackConfig);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [articuloDetalle, setArticuloDetalle] = useState(null);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [dialogError, setDialogError] = useState(null);
  const [nuevoComentario, setNuevoComentario] = useState('');
  const [likeLoading, setLikeLoading] = useState(false);
  const [commentLoading, setCommentLoading] = useState(false);
  const comentariosSectionRef = useRef(null);
  const comentarioInputRef = useRef(null);
  const selectedFontFamily = fontOptions.find((font) => font.id === config.tipoLetra)?.family || '"Poppins", sans-serif';

  useEffect(() => {
    let active = true;

    const resolveSubdomain = () => {
      if (typeof window === 'undefined') return 'adminnutrilander';
      const params = new URLSearchParams(window.location.search);
      const fromQuery = params.get('subdomain');
      const hostname = window.location.hostname;
      const fromHost = hostname?.split('.')[0];
      return fromQuery || fromHost || 'adminnutrilander';
    };

    const fetchBlogData = async () => {
      setLoading(true);
      setError(null);
      try {
        const subdominio = resolveSubdomain();
        const response = await API.get(`/nutriologo/subdomain/info?subdominio=${subdominio}`);
        const result = response.data?.nutriologoInfo || response.data;
        if (!result) {
          throw new Error('No se encontró información del nutriólogo.');
        }

        const displayName = result.nombreAMostrar || `${result.usuario?.nombre || ''} ${result.usuario?.apellidos || ''}`.trim() || 'Nutriólogo';
        const displayNutriologo = {
          id: result._id,
          nombre: displayName,
          subdomain: result.subdomain || result.usuario?.subdominio || 'nutriologo',
          imagen: result.usuario?.imagen || result.imagen || fallbackNutriologo.imagen,
          telefonoAMostrar: result.telefonoAMostrar,
          urlFacebook: result.urlFacebook,
        };

        const blogConfig = result.blog || {};
        const resolvedConfig = {
          diseno: blogConfig.diseno || fallbackConfig.diseno,
          color: blogConfig.color || fallbackConfig.color,
          tipoLetra: blogConfig.tipoLetra || fallbackConfig.tipoLetra,
        };

        const articulosResponse = await API.get(`/blogcomunidad/articulos/autor/${result._id}`);
        const propios = (articulosResponse.data || [])
          .map((art) => ({
            ...art,
            esCompartido: false,
          }))
          .filter(isArticuloVisible);

        let compartidos = [];
        const sharedIds = (blogConfig.articulosCompartidos || [])
          .map((item) => (typeof item === 'object' ? item._id : item))
          .filter(Boolean);
        if (sharedIds.length > 0) {
          const sharedResults = await Promise.all(
            sharedIds.map((id) =>
              API.get(`/blogcomunidad/articulos/${id}`)
                .then((res) => res.data && { ...res.data, esCompartido: true })
                .catch(() => null),
            ),
          );
          compartidos = sharedResults.filter((art) => art && isArticuloVisible(art));
        }

        const combined = [...propios, ...compartidos].filter(isArticuloVisible);
        const uniqueMap = new Map();
        combined.forEach((article) => {
          if (article?._id && !uniqueMap.has(article._id)) {
            uniqueMap.set(article._id, article);
          }
        });
        const dedupedArticles = uniqueMap.size ? Array.from(uniqueMap.values()) : combined;

        if (active) {
          setNutriologo(displayNutriologo);
          setConfig(resolvedConfig);
          setArticulos(dedupedArticles);
        }
      } catch (err) {
        if (active) {
          setError(err.message || 'No se pudieron cargar los datos del blog.');
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    fetchBlogData();
    return () => {
      active = false;
    };
  }, [fallbackConfig, fallbackNutriologo]);

  const emptyState = (
    <Box
      sx={{
        textAlign: 'center',
        py: { xs: 6, md: 8 },
        px: { xs: 3, md: 6 },
        background: '#fff',
        borderRadius: 4,
        boxShadow: '0 24px 48px rgba(15,23,42,0.12)',
      }}>
      <ArticleIcon sx={{ fontSize: 72, color: config.color, mb: 2 }} />
      <Typography variant='h6' sx={{ fontWeight: 700, mb: 1, color: '#0f172a' }}>
        Este nutriólogo aún no tiene artículos
      </Typography>
      <Typography sx={{ color: '#64748b', maxWidth: 480, mx: 'auto' }}>Próximamente encontrarás contenido creado por {nutriologo?.nombre || 'tu nutriólogo'} en este espacio.</Typography>
    </Box>
  );

  const scrollToComentarios = useCallback(() => {
    if (comentariosSectionRef.current) {
      comentariosSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  const cargarDetalleArticulo = useCallback(async (id) => {
    const { data } = await API.get(`/blogcomunidad/articulos/${id}`);
    return data;
  }, []);

  const handleOpenArticulo = useCallback(
    async (articulo, options = { scrollComments: false }) => {
      setDialogOpen(true);
      setDialogError(null);
      setDialogLoading(true);
      setArticuloDetalle(null);
      try {
        const data = await cargarDetalleArticulo(articulo._id);
        setArticuloDetalle(data);
        if (options.scrollComments) {
          setTimeout(scrollToComentarios, 300);
        }
        comentarioInputRef.current?.focus();
      } catch (err) {
        setDialogError('No se pudo cargar el artículo');
      } finally {
        setDialogLoading(false);
      }
    },
    [cargarDetalleArticulo, scrollToComentarios],
  );

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setArticuloDetalle(null);
    setNuevoComentario('');
    setDialogError(null);
  };

  const handlePreviewComment = (article) => {
    setArticuloDetalle(article);
    setDialogOpen(true);
  };

  const previewProps = useMemo(
    () => ({
      color: config.color,
      fontFamily: selectedFontFamily,
      data: {
        articulos,
        nutriologo,
      },
      onArticuloClick: handleOpenArticulo,
      onComment: handlePreviewComment,
      emptyState,
    }),
    [articulos, config.color, emptyState, nutriologo, selectedFontFamily, handleOpenArticulo, handlePreviewComment],
  );

  const activeDesign = config.diseno;
  const isClassic = activeDesign === 1;

  const preview = useMemo(() => {
    if (activeDesign === 1) return <ClassicPreview {...previewProps} />;
    if (activeDesign === 2) return <MinimalPreview {...previewProps} />;
    return <ModernPreview {...previewProps} />;
  }, [previewProps, activeDesign]);

  if (loading) {
    return (
      <Box component='main' bgcolor='#f5f7fb' py={6}>
        <Container maxWidth='lg'>
          <Box display='flex' justifyContent='center' py={8}>
            <CircularProgress />
          </Box>
        </Container>
      </Box>
    );
  }

  const hasArticles = articulos.length > 0;

  const content = (
    <>
      {error && (
        <Typography
          variant='body2'
          color='error'
          sx={{
            mb: 2,
            ...(isClassic ? { px: { xs: 2, md: 6 }, pt: 4 } : {}),
          }}>
          {error}
        </Typography>
      )}
      {preview}
    </>
  );

  return (
    <Box component='main' bgcolor='#f5f7fb' py={isClassic ? 0 : activeDesign === 2 ? 0 : 6} sx={{ overflowX: isClassic ? 'hidden' : activeDesign === 2 ? 'hidden' : 'visible' }}>
      {isClassic ? content : <Container maxWidth={activeDesign === 2 ? 'xl' : activeDesign === 3 ? 'xl' : 'lg'}>{content}</Container>}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth='md' fullWidth>
        {dialogLoading ? (
          <DialogContent sx={{ minHeight: 240, display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
            <CircularProgress />
          </DialogContent>
        ) : articuloDetalle ? (
          <>
            <DialogTitle sx={{ borderBottom: '1px solid #e5e7eb', pb: 1 }}>
              <Box display='flex' justifyContent='space-between' alignItems='center'>
                <Typography variant='h5' sx={{ fontWeight: 700, flex: 1 }}>
                  {articuloDetalle.titulo}
                </Typography>
                <Box display='flex' alignItems='center' ml={2}>
                  <Stack direction='row' spacing={2} alignItems='center' sx={{ color: '#475569', fontSize: '0.85rem' }}>
                    <Stack direction='row' spacing={0.5} alignItems='center'>
                      <Favorite color='inherit' sx={{ fontSize: 16 }} />
                      <Typography component='span'>{articuloDetalle.numeroLikes || 0}</Typography>
                    </Stack>
                    <IconButton onClick={() => comentariosSectionRef.current?.scrollIntoView({ behavior: 'smooth' })} sx={{ color: '#475569' }}>
                      <Comment sx={{ fontSize: 16 }} />
                      <Typography component='span' sx={{ ml: 0.5, fontSize: '0.85rem' }}>
                        {articuloDetalle.comentarios?.length || 0}
                      </Typography>
                    </IconButton>
                  </Stack>
                  <IconButton onClick={handleCloseDialog}>
                    <Close />
                  </IconButton>
                </Box>
              </Box>
            </DialogTitle>
            <DialogContent sx={{ px: { xs: 2, md: 4 }, py: 3 }}>
              <Box sx={{ position: 'sticky', top: 0, background: '#fff', zIndex: 1, pb: 1, mb: 1 }}>
                <Box display='flex' alignItems='center' gap={2} sx={{ pt: 1 }}>
                  {articuloDetalle.autorId?.usuario?.imagen ? (
                    <Avatar src={articuloDetalle.autorId.usuario.imagen} sx={{ width: 48, height: 48 }} />
                  ) : (
                    <Avatar sx={{ width: 48, height: 48 }}>
                      <Person />
                    </Avatar>
                  )}
                  <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', cursor: 'pointer', textDecoration: 'underline' }} onClick={() => (window.location.href = `https://${articuloDetalle.autorId?.subdomain}.nutrilander.com/blog`)}>
                      {getAutorNombre(articuloDetalle.autorId)}
                    </Typography>
                    <Typography sx={{ fontSize: '0.8rem', color: '#64748b', cursor: 'pointer', textDecoration: 'underline' }} onClick={() => (window.location.href = `https://${articuloDetalle.autorId?.subdomain}.nutrilander.com/blog`)}>
                      @{articuloDetalle.autorId?.subdomain}
                    </Typography>
                    <Typography sx={{ fontSize: '0.8rem', color: '#64748b' }}>{formatDate(articuloDetalle.createdAt)}</Typography>
                  </Box>
                </Box>
              </Box>
              {articuloDetalle.imagenUrl ? (
                <Box
                  component='img'
                  src={articuloDetalle.imagenUrl}
                  alt={articuloDetalle.titulo}
                  sx={{
                    width: '100%',
                    borderRadius: 2,
                    mb: 3,
                    maxHeight: 360,
                    objectFit: 'cover',
                  }}
                />
              ) : (
                <Box
                  sx={{
                    width: '100%',
                    height: 280,
                    borderRadius: 2,
                    mb: 3,
                    background: `linear-gradient(135deg, ${config.color}20 0%, ${config.color}35 100%)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                  }}>
                  <ArticleIcon sx={{ fontSize: 120, color: config.color }} />
                </Box>
              )}
              <Typography variant='h6' sx={{ fontWeight: 600, mb: 1 }}>
                {articuloDetalle.descripcion}
              </Typography>
              <Typography sx={{ fontSize: '0.95rem', lineHeight: 1.7, color: '#475569' }}>{articuloDetalle.articulo}</Typography>
              <Divider sx={{ my: 3 }} />
              <Box ref={comentariosSectionRef}>
                <Typography variant='h6' sx={{ fontWeight: 700, mb: 2 }}>
                  Comentarios ({articuloDetalle.comentarios?.length || 0})
                </Typography>
                {articuloDetalle.comentarios && articuloDetalle.comentarios.length > 0 ? (
                  [...articuloDetalle.comentarios]
                    .sort((a, b) => new Date(b.fecha) - new Date(a.fecha))
                    .map((comment, index) => (
                      <Box
                        key={index}
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          background: '#f8fafc',
                          mb: 1.5,
                        }}>
                        <Typography sx={{ fontWeight: 700 }}>{getAutorNombre(comment.nutriologoId)}</Typography>
                        <Typography sx={{ fontSize: '0.9rem', color: '#475569', mb: 1 }}>{comment.comentario}</Typography>
                        <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8' }}>{formatDateTime(comment.fecha)}</Typography>
                      </Box>
                    ))
                ) : (
                  <Typography color='textSecondary' sx={{ textAlign: 'center', p: 3 }}>
                    Aún no hay comentarios.
                  </Typography>
                )}
              </Box>
            </DialogContent>
          </>
        ) : (
          <DialogContent sx={{ minHeight: 200, display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
            <Typography color='error'>{dialogError || 'No se encontró el artículo'}</Typography>
          </DialogContent>
        )}
      </Dialog>
    </Box>
  );
};

export default BlogPreviewPage;
